import { memo, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react'
import { Box, useTheme } from '@mui/material'
import { makeStyles } from 'tss-react/mui'
import { PayoffLeg, PayoffProfitData, SdData } from '../payoffContainer/livePayoffContainer'
import { PayoffStrikeData } from '../../reducer/strategyBuilder.reducer'
import PayoffChart from './payoffChart/payoffChart'
import { getDefaultLayoutOptions, PAYOFF_LEGEND_LEFT_SCALE_OFFSET_PX } from '../../util/payoffChart'
import { PayoffChartDisplayOptions, StrikeClickInfo } from './payoffChart/payoffChart.typings'
import useSelectPrimaryOrUnderlyingSecurity from '../../hooks/useSelectPrimaryOrUnderlyingSecurity'
import { PayoffGraphContext } from './payoffGraphContext'
import PayoffGraphMetaDataDisplay from './PayoffGraphMetaDataDisplay'
import { PAYOFF_GRAPH_META_KEY, setPayoffGraphMetaData, clearPayoffGraphMetaData } from './payoffGraphMetaDataStore'
import PayoffSettingsDialog from '../payoffChart/PayoffSettingsDialog'
import PayoffChartSettingsOverlay from '../payoffChart/payoffChartSettingsOverlay'
import { useSelector } from 'react-redux'
import { selectIsEmbedded } from '../../selectors/device'
import { useUser } from '../../context/UserContext'
import { FeatureCategory, FeatureName } from '../adminUserRestrictions/Restrictions.constants'

export type PayoffChartWrapperProps = {
    orderSlidersElements: Record<string, HTMLDivElement>
    payoffLegs: PayoffLeg[]
    sdData: SdData[]
    breakevenPoints: number[]
    profitData: PayoffProfitData[]
    spotPrice: number
    payoffStrikes: PayoffStrikeData[]
    addLegEnabled: boolean
    showTimescale?: boolean
    showPnLScale?: boolean
    showOiBars?: boolean
    showSdLines?: boolean
    showSettingsButton?: boolean
    updateLegStrike: (legId: string, strike: number) => void
    onAddLegClick: (clickInfo: StrikeClickInfo) => void
}

const PayoffChartWrapper: React.FC<PayoffChartWrapperProps> = ({
    orderSlidersElements,
    payoffLegs,
    sdData,
    profitData,
    breakevenPoints,
    spotPrice,
    payoffStrikes,
    addLegEnabled,
    showOiBars = true,
    showTimescale = true,
    showPnLScale = true,
    showSdLines = true,
    showSettingsButton = true,
    updateLegStrike,
    onAddLegClick
}) => {
    const chartRef = useRef<PayoffChart>(null)
    const payoffGraphContext = useContext(PayoffGraphContext)
    const { classes } = useStyles()
    const theme = useTheme()
    const [container, setContainer] = useState<HTMLDivElement>(null)
    const { primaryOrUnderlying } = useSelectPrimaryOrUnderlyingSecurity()
    const crosshairDataRef = useRef({
        profitData,
        payoffStrikes,
        spotPrice,
        priceMultiplier: primaryOrUnderlying?.priceMultiplier ?? 100,
    })
    const [openSettingsDialog, setOpenSettingsDialog] = useState(false)
    const [settingsButtonWidth, setSettingsButtonWidth] = useState<number>(0)
    const [chartMountNode, setChartMountNode] = useState<HTMLDivElement | null>(null)
    const isEmbedded = useSelector(selectIsEmbedded)

    const legendLeft = useMemo(
        () =>
            showOiBars
                ? `calc(${theme.spacing(1)} + ${PAYOFF_LEGEND_LEFT_SCALE_OFFSET_PX}px)`
                : theme.spacing(1),
        [showOiBars, theme]
    )

    const updateRightScaleWidth = useCallback(() => {
        if (!chartRef.current) return
        setSettingsButtonWidth(chartRef.current.getPriceScaleWidth('right'))
    }, [])

    const handleOpenSettings = useCallback(() => setOpenSettingsDialog(true), [])
    const handleCloseSettings = useCallback(() => setOpenSettingsDialog(false), [])
    const setChartContainerRef = useCallback((node: HTMLDivElement | null) => {
        setContainer(node)
        setChartMountNode((prev) => (prev === node ? prev : node))
        payoffGraphContext?.setChartAnchorContainer?.(node)
    }, [payoffGraphContext])
    const {user:{hasAccess}} = useUser()

    const layoutOptions = useMemo(() => {
        if (!payoffStrikes) return
        return getDefaultLayoutOptions(theme, new Set([...payoffStrikes.map((s) => s.strike)]), showTimescale, showPnLScale, showOiBars, isEmbedded)
    }, [theme, payoffStrikes, showTimescale, showPnLScale, showOiBars, primaryOrUnderlying, isEmbedded])

    const displayOptions: PayoffChartDisplayOptions = useMemo(() => {
        return {
            showOI: showOiBars,
            showSdLines: showSdLines,
            showExistingProfitSeries: payoffLegs.some(p=>p.type==='position')
        }
    }, [showOiBars, showSdLines, payoffLegs])

    useEffect(() => {
        crosshairDataRef.current = {
            profitData,
            payoffStrikes,
            spotPrice,
            priceMultiplier: primaryOrUnderlying?.priceMultiplier ?? 100,
        }
    }, [profitData, payoffStrikes, spotPrice, primaryOrUnderlying])

    const handleCrosshairMove = useCallback((nearestStrike: number, time: any) => {
        const { profitData: currentProfitData, payoffStrikes: currentPayoffStrikes, spotPrice: currentSpotPrice, priceMultiplier } =
            crosshairDataRef.current

        if (!currentProfitData || !currentPayoffStrikes) {
            return
        }

        let displayStrike: number
        let strikeData: PayoffStrikeData | undefined
        let profitDataPoint: PayoffProfitData | undefined

        if (!time) {
            const spotStrike = Math.floor(currentSpotPrice / priceMultiplier)
            strikeData = currentPayoffStrikes.reduce((prev, curr) =>
                Math.abs(curr.strike - spotStrike) < Math.abs(prev.strike - spotStrike) ? curr : prev
            )
            displayStrike = strikeData.strike
            profitDataPoint =
                currentProfitData.length === 0
                    ? undefined
                    : currentProfitData.find((p) => p.price === displayStrike) ||
                      currentProfitData.reduce((prev, curr) =>
                          Math.abs(curr.price - displayStrike) < Math.abs(prev.price - displayStrike) ? curr : prev
                      )
        } else {
            const timeValue = typeof time === 'number' ? time : Number(time)
            strikeData = currentPayoffStrikes.find((s) => s.strike === nearestStrike)
            displayStrike = nearestStrike
            profitDataPoint = currentProfitData.find((p) => p.price === timeValue)
        }

        if (strikeData && profitDataPoint) {
            setPayoffGraphMetaData(PAYOFF_GRAPH_META_KEY, {
                time: displayStrike,
                callOi: strikeData.callOi || 0,
                putOi: strikeData.putOi || 0,
                callDelta: strikeData.callDelta || 0,
                putDelta: strikeData.putDelta || 0,
                expiryPnl: profitDataPoint.expiryProfit / priceMultiplier,
                expectedPnl: profitDataPoint.expectedProfit / priceMultiplier,
            })
        }
    }, [])

    useEffect(() => {
        if (chartRef.current || !container || !layoutOptions || !profitData) return
        chartRef.current = new PayoffChart({
            orderSlidersElements,
            underlying: primaryOrUnderlying,
            container,
            payoffLegs,
            payoffStrikes,
            sdData,
            profitData,
            spotPrice,
            breakevenPoints,
            layoutOptions,
            theme,
            displayOptions,
            isEmbedded,
            interactionsEnabled: hasAccess(FeatureCategory.STRATEGY_BUILDER, '', FeatureName.PAYOFF_CHART_INTERACTIONS),
            callbacks: {
                updateLeg: updateLegStrike,
                onAddLegClick,
                onCrosshairMove: handleCrosshairMove
            }
        })
        payoffGraphContext.setPayoffChart(chartRef.current)
        setTimeout(updateRightScaleWidth, 100)
    }, [
        container,
        layoutOptions,
        showOiBars,
        showSdLines,
        displayOptions,
        payoffLegs,
        payoffStrikes,
        sdData,
        profitData,
        breakevenPoints,
        spotPrice,
        primaryOrUnderlying,
        payoffGraphContext,
        updateLegStrike,
        onAddLegClick,
        updateRightScaleWidth,
        hasAccess
    ])

    useEffect(()=> {
        if(hasAccess(FeatureCategory.STRATEGY_BUILDER, '', FeatureName.PAYOFF_CHART_INTERACTIONS)){
            if(chartRef.current) {
                chartRef.current.interactionsEnabled = true
            }
        }
    }, [hasAccess])

    useEffect(() => {
        if (!chartRef.current || !layoutOptions) return
        chartRef.current.chartApi.applyOptions(layoutOptions)
        const id = setTimeout(updateRightScaleWidth, 100)
        return () => clearTimeout(id)
    }, [layoutOptions, updateRightScaleWidth])

    useEffect(() => {
        if (!container) return
        const observer = new ResizeObserver(() => updateRightScaleWidth())
        observer.observe(container)
        return () => observer.disconnect()
    }, [container, updateRightScaleWidth])

    useEffect(() => {
        if (!chartRef.current || !theme) return
        chartRef.current.theme = theme
    }, [theme])

    useEffect(() => {
        return () => {
            chartRef.current && chartRef.current.destroy()
            chartRef.current = null
            clearPayoffGraphMetaData(PAYOFF_GRAPH_META_KEY)
        }
    }, [])

    useEffect(() => {
        if (!chartRef.current) return
        chartRef.current.setAddOrderEnabled(addLegEnabled)
    }, [addLegEnabled])

    useEffect(() => {
        if (!chartRef.current) return
        chartRef.current.setDisplayOptions(displayOptions)
    }, [displayOptions])

    useEffect(() => {
        if (!chartRef.current) return
        chartRef.current.updateCallback('updateLeg', updateLegStrike)
    }, [updateLegStrike])

    useEffect(() => {
        if (!chartRef.current) return
        chartRef.current.updateCallback('onAddLegClick', onAddLegClick)
    }, [onAddLegClick])

    useEffect(() => {
        if (!chartRef.current) return
        chartRef.current.updateCallback('onCrosshairMove', handleCrosshairMove)
    }, [handleCrosshairMove])

    useEffect(() => {
        if (!chartRef.current) return
        chartRef.current.updateProfitData(profitData, breakevenPoints)
    }, [profitData, breakevenPoints])

    useEffect(() => {
        if (!chartRef.current) return
        chartRef.current.updatePayoffStrikes(payoffStrikes)
    }, [payoffStrikes])

    useEffect(() => {
        if (!chartRef.current) return
        chartRef.current.updateSdLines(sdData, spotPrice)
    }, [sdData, spotPrice])

    useEffect(() => {
        if (!chartRef.current) return
        chartRef.current.updatePayoffLegs(payoffLegs)
    }, [payoffLegs])

    useEffect(() => {
        if (!chartRef.current) return
        chartRef.current.updateOrderSliderElements(orderSlidersElements)
    }, [orderSlidersElements])

    useEffect(() => {
        if (profitData && payoffStrikes && spotPrice) {
            handleCrosshairMove(0, null)
        }
    }, [profitData, payoffStrikes, spotPrice, handleCrosshairMove])

    return (
        <Box className={classes.chartWrapper} width="100%" height="100%">
            <Box
                className={classes.metaDataContainer}
                sx={{ left: legendLeft }}
            >
                <PayoffGraphMetaDataDisplay metaDataKey={PAYOFF_GRAPH_META_KEY} showOiBars={showOiBars} />
            </Box>
            <div className={classes.chartContainer} ref={setChartContainerRef} />
            <PayoffSettingsDialog open={openSettingsDialog} onClose={handleCloseSettings} />
            {showSettingsButton ? (
                <PayoffChartSettingsOverlay
                    container={chartMountNode}
                    readOnly={false}
                    width={settingsButtonWidth}
                    onOpen={handleOpenSettings}
                />
            ) : null}
        </Box>
    )
}

export default memo(PayoffChartWrapper)

export const useStyles = makeStyles()((theme) => {
    return {
        chartWrapper: {
            position: 'relative',
            width: '100%',
            height: '100%'
        },
        metaDataContainer: {
            position: 'absolute',
            top: theme.spacing(1),
            zIndex: 10,
            pointerEvents: 'none'
        },
        chartContainer: {
            position: 'relative',
            width: '100%',
            height: '100%'
        }
    }
})
